import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { shoppingListApi } from "@/services/shoppingListApi";
import { ShoppingListCreate } from "@/pages/ShoppingList/types.ts";
import { XIcon } from "lucide-react";

interface CreateListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateListModal = ({
  isOpen,
  onClose,
  onSuccess,
}: CreateListModalProps) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<
    Omit<ShoppingListCreate, "owner" | "sharedWith">
  >({
    name: "",
    type: "Alimentaire",
    items: [],
  });

  // Options pour les types de liste
  const listTypes = [
    "Alimentaire",
    "Outillage",
    "Fournitures",
    "Vêtements",
    "Électronique",
    "Autre",
  ];

  // Mutation pour créer une liste
  const createListMutation = useMutation({
    mutationFn: shoppingListApi.createList,
    onSuccess: (data) => {
      onSuccess();
      // Rediriger vers la nouvelle liste
      navigate({ to: `/house/shopping-list/${data._id}` });
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newList: ShoppingListCreate = {
      ...formData,
      owner: "",
      sharedWith: [],
    };

    createListMutation.mutate(newList);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-base-100 rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Créer une nouvelle liste</h2>
          <button className="btn btn-ghost btn-sm btn-circle" onClick={onClose}>
            <XIcon size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-control mb-4">
            <label className="label">
              <span className="label-text">Nom de la liste</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="input input-bordered w-full"
              placeholder="Ex: Courses semaine, Bricolage salon..."
              required
            />
          </div>

          <div className="form-control mb-6">
            <label className="label">
              <span className="label-text">Type de liste</span>
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="select select-bordered w-full"
              required
            >
              {listTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createListMutation.isPending}
            >
              {createListMutation.isPending ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                "Créer"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
